import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { map, Observable, switchMap } from 'rxjs';

// Components
import { AlertComponent } from '../../../../../../shared/_components/alert/alert.component';
import { ButtonComponent } from '../../../../../../shared/_components/button/button.component';
import { LoadingOtterComponent } from '../../../../../../shared/_components/loading-otter/loading-otter.component';
import { FormatFileSizePipe } from '../../../../../../shared/_pipes/format-file-size.pipe';
import { FlowMediaUploadStep } from '../../flows/flow-media-upload/step/flow-media-upload-step.component';

// Models
import { IntegrationProfile, ProviderLimitsResponse, ProviderMediaLimits } from '../../../../../models/integration-profile.model';

// Icons
import IconLibrary from '../../../../../../shared/_libraries/icon-library.helper';

const PROVIDER_IMAGE_LIMITS: Record<string, number> = {
  x: 4,
  facebook: 10,
  instagram: 10,
  linkedin: 20,
  youtube: 1,
};

@Component({
  selector: 'pp-step-media-video-integrations',
  templateUrl: './step-media-video-integrations.component.html',
  styleUrl: './step-media-video-integrations.component.scss',
  imports: [CommonModule, FontAwesomeModule, AlertComponent, ButtonComponent, FormatFileSizePipe, LoadingOtterComponent],
})
export class StepMediaVideoIntegrationsComponent extends FlowMediaUploadStep implements OnInit {

  // Component handler (!IMPORTANT - Must match step identifier for this flow)
  identifier = 'mediaUploadFlowStepVideoIntegrations';

  // Integrations
  enabledIntegrationsList$?: Observable<IntegrationProfile[]>;
  disabledIntegrationsList$?: Observable<IntegrationProfile[]>;
  integrationsListLoading$?: Observable<boolean>;

  // Provider upload limits
  providerLimits: ProviderLimitsResponse = {};

  // Icons
  public iconLibrary = IconLibrary;

  ngOnInit(): void {
    // Retrieve the available integrations
    const connectedIntegrations$ = this.mediaUploadService.activeIntegrationsList$.pipe(
      map(integrations => integrations.filter(integration => integration.active)),
    );

    // Retrieve the enabled integrations
    this.enabledIntegrationsList$ = connectedIntegrations$.pipe(
      map(integrations => integrations.filter(integration => !integration.disabled)),
    );

    // Retrieve the disabled integrations
    this.disabledIntegrationsList$ = connectedIntegrations$.pipe(
      map(integrations => integrations.filter(integration => integration.disabled)),
    );

    // Check whether the retrieval of the list of integrations is in progress
    this.integrationsListLoading$ = this.mediaUploadService.activeIntegrationsListLoading$;

    // Retrieve integrations, then fetch their upload limits
    this.mediaUploadService.getActiveIntegrations().pipe(
      switchMap(() => this.mediaUploadService.getConnectedProviderLimits()),
    ).subscribe({
      next: limits => {
        this.providerLimits = limits;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Returns the reason that the integration has been marked as disabled.
   * @param integration the integration.
   * @returns the reason for the integration to have been marked as disabled. If not available, provides a generic description.
   * @memberof StepMediaVideoIntegrationsComponent
   */
  getIntegrationDisabledReason(integration: IntegrationProfile): string {
    return integration.disabledReason || 'This integration is currently unavailable for this upload.';
  }

  /**
   * Checks if an integration has been marked as disabled.
   * @param integration the integration.
   * @returns `true` if the integration is disabled, `false` otherwise.
   * @memberof StepMediaVideoIntegrationsComponent
   */
  isIntegrationDisabled(integration: IntegrationProfile): boolean {
    return integration.disabled === true;
  }

  /**
   * Checks if an integration has been selected.
   * @param integrationId the identifier of the integration.
   * @returns `true` if the integration has been selected, `false` otherwise.
   * @memberof StepMediaVideoIntegrationsComponent
   */
  isIntegrationSelected(integrationId: string): boolean {
    return this.selectedIntegrations.some(integration => integration.id === integrationId);
  }

  /**
   * Submits the list of selected integrations and navigates to the next step.
   * @memberof StepMediaVideoIntegrationsComponent
   */
  submitAndGoToNextStep(): void {
    // Post the selected integrations
    this.mediaUploadService.storeSelectedIntegrations(this.selectedIntegrations).subscribe({
      next: () => this.goToNextStep(),
    });
  }

  /**
   * Updates the list of integration providers to share to. Integrations added to this list will dynamically upload the flow's steps.
   * @param integration the integration.
   * @memberof StepMediaVideoIntegrationsComponent
   */
  getProviderMediaLimits(integration: IntegrationProfile): ProviderMediaLimits | null {
    const limits = this.providerLimits[integration.id];
    if (!limits) return null;
    return limits[this.previewMediaType] ?? null;
  }

  getProviderImageWarning(integration: IntegrationProfile): string | null {
    if (!this.mediaUploadService.isGroupUpload) return null;
    const fileCount = this.mediaUploadService.groupAssetCount;
    const limit = PROVIDER_IMAGE_LIMITS[integration.id] ?? 10;
    if (fileCount > limit) {
      return `Only the first ${limit} ${limit === 1 ? 'image' : 'images'} will be posted to ${integration.label}.`;
    }
    return null;
  }

  updateSelectedItemList(integration: IntegrationProfile): void {
    // If the integration is disabled, do not process
    if (this.isIntegrationDisabled(integration)) return;

    // If the integration has previously been selected, remove it from the selected list
    if (this.selectedIntegrations.some(item => item.id === integration.id)) {
      this.mediaUploadService.removeSelectedIntegration(integration.id);
      return;
    }

    // Add the selected integration to the list
    this.mediaUploadService.addSelectedIntegration(integration.id);
  }
}
